// frontend/src/components/UserSettings.js - Themed version with professional design
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import {
  Settings,
  User,
  Shield,
  Bell,
  Building,
  Users,
  ChevronRight,
  Menu,
  X,
  Database,
  MessageSquare
} from 'lucide-react';
import { isSystemAdmin, isAdmin, getRolesAtOrAbove, ROLE_HIERARCHY } from '../utils/roleUtils';
import { shouldShowNotificationMenu, shouldShowNotificationManagementMenu } from './notifications/notificationUtils';
import { shouldShowSurveyManagementMenu, shouldShowSurveysForUser } from './surveys/surveyUtils';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard } from './themed';
import AccountSettings from './AccountSettings';
import PreferenceSettings from './PreferenceSettings';
import SecuritySettings from './SecuritySettings';
import OrganizationSettings from './OrganizationSettings';
import UserManagement from './UserManagement';
import SystemAdministration from './SystemAdministration';
import SurveyConfiguration from './surveys/SurveyConfiguration';
import VoluntarySurvey from './surveys/VoluntarySurvey';
import NotificationSettings from './NotificationSettings';
import NotificationManagement from './NotificationManagement';
import { usePreferences } from '../contexts/PreferenceContext';

const UserSettings = ({ userRole, entities, currentEntity, onEntityPreferenceSuccess }) => {
  const { t } = useTranslation();
  const { background, text, border, components, cn } = useTheme();
  const { systemSettings } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('account');
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [showNotificationManagementMenu, setShowNotificationManagementMenu] = useState(false);
  const [showSurveyMenu, setShowSurveyMenu] = useState(false);
  const [showUserSurveyMenu, setShowUserSurveyMenu] = useState(false);

  // Extract the current section from the URL
  useEffect(() => {
    const path = location.pathname.split('/').pop();
    if (path && path !== 'settings') {
      setActiveSection(path);
    }
  }, [location]);

  // Check notification menu visibility with the new API
  useEffect(() => {
    const checkNotificationVisibility = async () => {
      const entityId = localStorage.getItem('entityId');
      if (entityId) {
        try {
          // Check both user notification settings visibility and management visibility
          const [userMenuVisible, managementMenuVisible] = await Promise.all([
            shouldShowNotificationMenu(entityId),
            shouldShowNotificationManagementMenu(entityId, userRole)
          ]);

          setShowNotificationMenu(userMenuVisible);
          setShowNotificationManagementMenu(managementMenuVisible);

          console.log('[UserSettings] Notification menu visibility:', {
            entityId,
            userRole,
            showUserMenu: userMenuVisible,
            showManagementMenu: managementMenuVisible
          });
        } catch (error) {
          console.error('Error checking notification visibility:', error);
          setShowNotificationMenu(false);
          setShowNotificationManagementMenu(false);
        }
      } else {
        // No entity selected - hide notification menus
        setShowNotificationMenu(false);
        setShowNotificationManagementMenu(false);
      }
    };

    checkNotificationVisibility();
  }, [systemSettings, userRole]);

  // Check survey management menu visibility (admin menu - based on role)
  useEffect(() => {
    const checkSurveyVisibility = async () => {
      const entityId = localStorage.getItem('entityId');
      if (entityId) {
        try {
          // Use the specific function for Survey Management menu (admin menu)
          const visible = await shouldShowSurveyManagementMenu(entityId);
          setShowSurveyMenu(visible);

          console.log('[UserSettings] Survey management menu visibility:', {
            entityId,
            showMenu: visible
          });
        } catch (error) {
          console.error('Error checking survey management menu visibility:', error);
          setShowSurveyMenu(false);
        }
      } else {
        // No entity selected - hide survey menu
        setShowSurveyMenu(false);
      }
    };

    checkSurveyVisibility();
  }, [systemSettings]);

  // Check end user survey menu visibility (for taking surveys - no role checking)
  useEffect(() => {
    const checkUserSurveyVisibility = async () => {
      const entityId = localStorage.getItem('entityId');
      if (entityId) {
        try {
          // Use the specific function for end user surveys (no role checking)
          const visible = await shouldShowSurveysForUser(entityId);
          setShowUserSurveyMenu(visible);

          console.log('[UserSettings] End user survey menu visibility:', {
            entityId,
            showUserSurveyMenu: visible
          });
        } catch (error) {
          console.error('Error checking end user survey menu visibility:', error);
          setShowUserSurveyMenu(false);
        }
      } else {
        // No entity selected - hide user survey menu
        setShowUserSurveyMenu(false);
      }
    };

    checkUserSurveyVisibility();
  }, [systemSettings]);

  // Role arrays replaced with roleUtils functions

  const menuItems = [
    // MY ACCOUNT Section
    {
      id: 'account',
      label: t('myAccount'),
      icon: User,
      path: '/settings/account',
      roles: ROLE_HIERARCHY,
      section: 'account'
    },

    // PREFERENCES Section
    {
      id: 'preferences',
      label: t('preferences'),
      icon: Settings,
      path: '/settings/preferences',
      roles: ROLE_HIERARCHY,
      section: 'preferences'
    },
    ...(showNotificationMenu ? [{
      id: 'notifications',
      label: t('notifications'),
      icon: Bell,
      path: '/settings/notifications',
      roles: ROLE_HIERARCHY,
      section: 'preferences'
    }] : []),
    ...(showUserSurveyMenu ? [{
      id: 'feedback-surveys',
      label: t('feedbackSurveys'),
      icon: MessageSquare,
      path: '/settings/feedback-surveys',
      roles: ROLE_HIERARCHY,
      section: 'preferences'
    }] : []),
    {
      id: 'security',
      label: t('security'),
      icon: Shield,
      path: '/settings/security',
      roles: ROLE_HIERARCHY,
      section: 'preferences'
    },

    // ADMINISTRATION Section
    {
      id: 'organization',
      label: t('organizationSettings'),
      icon: Building,
      path: '/settings/organization',
      roles: getRolesAtOrAbove('admin'),
      section: 'administration'
    },
    ...(showSurveyMenu ? [{
      id: 'surveys',
      label: t('surveyManagement'),
      icon: MessageSquare,
      path: '/settings/surveys',
      roles: getRolesAtOrAbove('admin'),
      section: 'administration'
    }] : []),
    ...(showNotificationManagementMenu ? [{
      id: 'notification-management',
      label: t('notificationManagement'),
      icon: Bell,
      path: '/settings/notification-management',
      roles: getRolesAtOrAbove('admin'),
      section: 'administration'
    }] : []),
    {
      id: 'users',
      label: isAdmin(userRole)
        ? t('userManagement')
        : t('inviteUsers'),
      icon: Users,
      path: '/settings/users',
      roles: ROLE_HIERARCHY,
      section: 'administration'
    },
    {
      id: 'system',
      label: t('systemAdministration'),
      icon: Database,
      path: '/settings/system',
      roles: getRolesAtOrAbove('super_admin'),
      condition: () => isSystemAdmin(userRole),
      section: 'administration'
    }
  ];

  // Filter menu items based on user role and additional conditions
  const visibleMenuItems = menuItems.filter(item => {
    const hasRole = item.roles.includes(userRole);
    const passesCondition = !item.condition || item.condition();
    return hasRole && passesCondition;
  });

  const handleMenuItemClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={cn("min-h-screen", background.secondary)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className={cn("text-3xl font-bold", text.primary)}>{t('settings')}</h1>
          <p className={cn("mt-2", text.muted)}>{t('manageYourAccountAndPreferences')}</p>
        </div>

        <ThemedCard className="overflow-hidden">
          <div className="lg:grid lg:grid-cols-12">
            {/* Mobile menu button */}
            <div className={cn("lg:hidden p-4", border.default, "border-b")}>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={cn(
                  "flex items-center justify-between w-full text-left",
                  "transition-colors",
                  components.navigation.item
                )}
              >
                <span className={cn("text-lg font-medium", text.primary)}>{t('menu')}</span>
                {isMobileMenuOpen ? 
                  <X size={24} className={text.secondary} /> : 
                  <Menu size={24} className={text.secondary} />
                }
              </button>
            </div>

            {/* Professional Sidebar Navigation */}
            <aside className={cn(
              isMobileMenuOpen ? 'block' : 'hidden',
              'lg:block lg:col-span-3 p-6 rounded-l-lg',
              background.secondary
            )}>
              <nav className="space-y-1">
                {visibleMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleMenuItemClick(item.path)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-md",
                        "transition-all duration-200 ease-in-out",
                        isActive
                          ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-l-4 border-blue-600"
                          : cn(components.navigation.item, "border-l-4 border-transparent hover:border-gray-300")
                      )}
                    >
                      <div className="flex items-center">
                        <Icon className={cn(
                          "mr-3 h-5 w-5",
                          isActive ? "text-blue-600 dark:text-blue-400" : text.muted
                        )} />
                        {item.label}
                      </div>
                      <ChevronRight className={cn(
                        "h-4 w-4 transition-transform",
                        isActive ? "text-blue-600 dark:text-blue-400" : text.muted
                      )} />
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Main content area */}
            <main className={cn("lg:col-span-9 p-6", background.primary)}>
              <Routes>
                <Route path="account" element={<AccountSettings />} />
                <Route
                  path="preferences"
                  element={(
                    <PreferenceSettings
                      entities={entities}
                      currentEntity={currentEntity}
                      onEntityPreferenceSuccess={onEntityPreferenceSuccess}
                    />
                  )}
                />
                <Route path="security" element={<SecuritySettings />} />
                <Route path="notifications" element={<NotificationSettings />} />
                <Route
                  path="feedback-surveys"
                  element={
                    <VoluntarySurvey
                      entityId={currentEntity?.entityId || currentEntity?.id || localStorage.getItem('entityId')}
                    />
                  }
                />
                <Route path="notification-management" element={<NotificationManagement userRole={userRole} />} />
                <Route
                  path="organization"
                  element={
                    <OrganizationSettings
                      currentEntity={currentEntity}
                      userRole={userRole}
                    />
                  }
                />
                <Route
                  path="surveys"
                  element={
                    <SurveyConfiguration
                      entityId={currentEntity?.entityId || currentEntity?.id || localStorage.getItem('entityId')}
                    />
                  }
                />
                <Route 
                  path="users" 
                  element={
                    <UserManagement 
                      currentEntity={currentEntity}
                      userRole={userRole}
                    />
                  } 
                />
                <Route 
                  path="system" 
                  element={
                    <SystemAdministration 
                      userRole={userRole}
                    />
                  } 
                />
                <Route 
                  path="*" 
                  element={<AccountSettings />} 
                />
              </Routes>
            </main>
          </div>
        </ThemedCard>
      </div>
    </div>
  );
};

export default UserSettings;
